import { useQueryClient } from "react-query";
import { Link, RecordContextProvider, useGetOne } from "react-admin";
import {
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import type { Theme } from "@mui/material";
import { atom, useRecoilState } from "recoil";
import { TheoryExamWorkflow } from "../model/TheoryExamWorkflow";
import { Column } from "./Column";
import { Fragment, useCallback, useRef, useState } from "react";
import { LoadingButton } from "@mui/lab";
import { reportError } from "../backoffice.utils";
import { Row } from "./Row";
import { workflowsProvider } from "../providers/workflowsProvider";
import { deleteField, serverTimestamp } from "firebase/firestore";
import { StudentAvatar } from "./StudentAvatar";
import { StudentDisplayName } from "./StudentDisplayName";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { PracticalExamWorkflow } from "../model/PracticalExamWorkflow";
import { Student } from "../providers/studentsProvider";
import { DateTime } from "luxon";
import { renderAddress } from "./AddressDisplay";
import { InstructorAvatar } from "./InstructorAvatar";
import { InstructorDisplayName } from "./InstructorDisplayName";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { NotesList } from "../lists/NotesList";
import { DialogCloseButton } from "./DialogCloseButton";
import { useMenu } from "../hooks/useMenu";
import { formatDate } from "../utils/calendar";
import { EventInfoRow } from "../dialogs/EventDialog/EventInfoRow";
import CakeOutlinedIcon from "@mui/icons-material/CakeOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import { LicenseIcon } from "../icons/LicenseIcon";

export const workflowShownInWorkflowDialogState = atom<null | TheoryExamWorkflow | PracticalExamWorkflow>({
  key: "workflowShownInWorkflowDialog",
  default: null,
  effects: [
    ({ onSet }) => {
      onSet((newValue) => {
        const url = new URL(location.href);
        if (newValue) {
          if (url.searchParams.get("openDialog") !== `WorkflowDialog!${newValue.id}`) {
            url.searchParams.set("openDialog", `WorkflowDialog!${newValue.id}`);
            browserHistory.replace(url.toString());
          }
        } else if (url.searchParams.has("openDialog")) {
          url.searchParams.delete("openDialog");
          browserHistory.replace(url.toString());
        }
      });
    },
  ],
});

export function WorkflowDialog() {
  const queryClient = useQueryClient();
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  const [workflow, setWorkflowShownInDialog] = useRecoilState(workflowShownInWorkflowDialogState);
  const [formState, setFormState] = useState<{ [taskUid: string]: boolean }>({});
  const markedAsDone = (uid: string) => formState[uid] ?? workflow!.tasks[uid].isDone;
  const open = !!workflow;
  const dirty = workflow && Object.entries(formState).some(([uid, checked]) => checked !== workflow.tasks[uid].isDone);
  const studentUid = workflow?.workflowData.studentUid ?? "";
  const { data: student } = useGetOne<Student>("students", { id: studentUid }, { enabled: !!studentUid });
  const [saving, setSaving] = useState(false);
  const ref = useRef<{ showForm: () => void }>();

  const closeDialog = useCallback(() => {
    setFormState({});
    setWorkflowShownInDialog(null);
  }, [setFormState, setWorkflowShownInDialog]);

  async function save() {
    if (!workflow) {
      throw new Error(`Unexpected state: workflow: ${workflow}`);
    }
    let success = false;
    try {
      setSaving(true);
      const taskUids = Object.keys(workflow.tasks);
      const numFinishedTasks = taskUids.filter((uid) => markedAsDone(uid)).length;
      const isWorkflowFinished = numFinishedTasks === taskUids.length;
      await workflowsProvider.update("workflows", {
        id: workflow.id,
        data: {
          tasks: Object.fromEntries(Object.entries(formState).map(([uid, isDone]) => [uid, { isDone }])),
          finished: isWorkflowFinished,
          finishedAt: isWorkflowFinished ? serverTimestamp() : deleteField(),
        },
        previousData: workflow,
      });
      success = true;
    } catch (error) {
      reportError(`Failed to update workflow ${workflow?.id}`, error);
      showErrorToast("Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
    if (success) {
      closeDialog();
      void queryClient.invalidateQueries(["workflows"]);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={closeDialog}
      fullScreen={isMobile}
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: { borderRadius: { xs: 0, sm: "8px" } } }}
    >
      <DialogTitle sx={{ pr: "112px" }}>
        {workflow?.type === "theoryExamSignUp"
          ? "Theorieprüfung"
          : workflow?.type === "practicalExamSignUp"
            ? "Praktische Prüfung"
            : ""}
      </DialogTitle>
      {workflow && <_PopupMenu workflow={workflow} closeDialog={closeDialog} />}
      <DialogCloseButton onClick={closeDialog} />
      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          fontSize: "14px",
          minHeight: 0,
          overflowX: "hidden",
          overflowY: { xs: "auto", md: "hidden" },
          px: { xs: 2, sm: 3 },
          "& .MuiTypography-root": { fontSize: "14px" },
        }}
      >
        <Row
          gap={{ xs: 2, md: 3 }}
          sx={{
            display: { xs: "flex", md: "grid" },
            flex: { xs: "0 0 auto", md: "1 1 auto" },
            flexDirection: { xs: "column", md: "row" },
            gridTemplateColumns: { md: "minmax(0, 3fr) minmax(0, 2fr)" },
            minHeight: { xs: "auto", md: 0 },
            minWidth: 0,
            overflow: { xs: "visible", md: "hidden" },
          }}
        >
          <Column
            sx={{
              flex: { md: "2 1 0%" },
              minHeight: 0,
              minWidth: 0,
              overflowY: { md: "auto" },
              "& .MuiFormControlLabel-root": { mr: 0 },
              "& .MuiFormControlLabel-label": { minWidth: 0, overflowWrap: "anywhere" },
            }}
          >
            {workflow && <_WorkflowData workflow={workflow} />}
            {workflow && (
              <Column
                sx={{
                  backgroundColor: "action.hover",
                  borderRadius: "8px",
                  boxSizing: "border-box",
                  mt: 2,
                  p: 2,
                  width: "100%",
                  "& .MuiCheckbox-root": { p: 0.75 },
                }}
              >
                <Typography variant="body2" fontWeight="bold" color="text.primary" sx={{ mb: 0.5 }}>
                  Workflow
                </Typography>
                <Column sx={{ pl: 0.5 }}>
                  {workflow.taskTree.map(({ uid, subtasks }) => {
                    const task = workflow.tasks[uid];
                    const numFinishedSubtasks = subtasks.filter((it) => markedAsDone(it.uid)).length;
                    return (
                      <Fragment key={uid}>
                        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                          <FormControlLabel
                            key={uid}
                            control={
                              <Checkbox
                                disabled={saving}
                                checked={formState[uid] ?? task.isDone}
                                onChange={(event) => {
                                  setFormState({
                                    ...formState,
                                    [uid]: event.target.checked,
                                  });
                                }}
                              />
                            }
                            label={
                              subtasks.length > 0 ? (
                                <Row sx={{ flexWrap: "wrap" }}>
                                  <Typography>{task.title}</Typography>
                                  <Chip
                                    label={`${numFinishedSubtasks} / ${subtasks.length}`}
                                    size="small"
                                    sx={{
                                      color: "text.secondary",
                                      fontSize: "0.75rem",
                                      height: 20,
                                      ml: 1,
                                      "& .MuiChip-label": { px: 0.75 },
                                    }}
                                  />
                                </Row>
                              ) : (
                                task.title
                              )
                            }
                          />
                        </Box>
                        {subtasks.map(({ uid }) => {
                          const subtask = workflow.tasks[uid];
                          return (
                            <FormControlLabel
                              key={uid}
                              sx={{ paddingLeft: "30px" }}
                              control={
                                <Checkbox
                                  disabled={saving}
                                  checked={formState[uid] ?? subtask.isDone}
                                  onChange={(event) => {
                                    setFormState({
                                      ...formState,
                                      [uid]: event.target.checked,
                                    });
                                  }}
                                />
                              }
                              label={subtask.title}
                            />
                          );
                        })}
                      </Fragment>
                    );
                  })}
                </Column>
              </Column>
            )}
          </Column>
          {student && (
            <RecordContextProvider value={student}>
              <Box
                sx={{
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1,
                  boxSizing: "border-box",
                  display: "flex",
                  flex: { md: "1 1 0%" },
                  flexDirection: "column",
                  maxHeight: { xs: "50vh", md: "100%" },
                  minHeight: 0,
                  minWidth: 0,
                  overflow: "hidden",
                  p: 2,
                }}
              >
                <Typography variant="body2" fontWeight="bold" sx={{ flexShrink: 0 }}>
                  Notizen
                </Typography>
                <Box
                  sx={{
                    minHeight: 0,
                    minWidth: 0,
                    overflowY: "auto",
                    overscrollBehavior: "contain",
                    "& .MuiList-root": { mt: 1.5 },
                  }}
                >
                  <NotesList resource="studentNotes" ref={ref} style={{ minWidth: 0, overflowWrap: "anywhere" }} />
                </Box>
              </Box>
            </RecordContextProvider>
          )}
        </Row>
      </DialogContent>
      <DialogActions>
        <LoadingButton variant="contained" loading={saving} disabled={!dirty} onClick={save}>
          Speichern
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}

function _WorkflowData({ workflow }: { workflow: TheoryExamWorkflow | PracticalExamWorkflow }) {
  const { studentUid, preferredDates, preferredTimeOfDay, examLocation } = workflow.workflowData;
  const { data: student } = useGetOne<Student>("students", { id: studentUid });
  const bookedTraining =
    workflow.type === "practicalExamSignUp"
      ? student?.bookedTrainings.find((it) => it.id === workflow.workflowData.bookedTrainingId)
      : undefined;
  return (
    <Column
      spacing="0.5em"
      sx={{ bgcolor: "action.hover", borderRadius: 1, color: "text.secondary", p: 2, "& a": { color: "inherit" } }}
    >
      <Typography variant="body2" fontWeight="bold" color="text.primary">
        Details
      </Typography>
      <Box
        sx={{
          display: "grid",
          gap: { xs: "0.5em", md: 2 },
          gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "repeat(2, minmax(0, 1fr))" },
        }}
      >
        <Column spacing="0.5em" sx={{ minWidth: 0 }}>
          <EventInfoRow>
            <Link
              to={`/students/${studentUid}`}
              target="_blank"
              sx={{ alignItems: "center", display: "flex", minWidth: 0, textDecoration: "none" }}
            >
              <Tooltip title="Fahrschüler" arrow placement="top">
                <span style={{ display: "inline-flex" }}>
                  <StudentAvatar studentId={studentUid} size="20px" />
                </span>
              </Tooltip>
              <StudentDisplayName
                variant="body2"
                studentId={studentUid}
                sx={{ ml: "0.5em", overflowWrap: "anywhere" }}
              />
              <OpenInNewIcon sx={{ flexShrink: 0, fontSize: "16px", ml: "0.5em" }} />
            </Link>
          </EventInfoRow>
          {student?.dateOfBirth && (
            <EventInfoRow>
              <Tooltip title="Geburtsdatum" arrow placement="top">
                <CakeOutlinedIcon fontSize="small" sx={{ color: "text.secondary" }} />
              </Tooltip>
              <Typography variant="body2">{formatDate(student.dateOfBirth)}</Typography>
            </EventInfoRow>
          )}
          {bookedTraining && (
            <EventInfoRow>
              <Tooltip title="Führerscheinklasse" arrow placement="top">
                <LicenseIcon fontSize="small" sx={{ color: "text.secondary" }} />
              </Tooltip>
              <Typography variant="body2">Klasse {bookedTraining.drivingLicenseClass}</Typography>
            </EventInfoRow>
          )}
          {workflow.type === "practicalExamSignUp" && (
            <EventInfoRow>
              <Column spacing="4px">
                {(student?.instructorIds ?? []).length === 0 && (
                  <Typography variant="body2">{student ? "Kein Fahrlehrer zugewiesen" : "..."}</Typography>
                )}
                {(student?.instructorIds ?? []).map((instructorId) => (
                  <Link
                    key={instructorId}
                    to={`/instructors/${instructorId}/calendar`}
                    target="_blank"
                    sx={{ alignItems: "center", display: "flex", minWidth: 0, textDecoration: "none" }}
                  >
                    <Tooltip title="Fahrlehrer" arrow placement="top">
                      <span style={{ display: "inline-flex" }}>
                        <InstructorAvatar instructorId={instructorId} size="20px" />
                      </span>
                    </Tooltip>
                    <InstructorDisplayName
                      instructorId={instructorId}
                      variant="body2"
                      sx={{ ml: "0.5em", overflowWrap: "anywhere" }}
                    />
                    <OpenInNewIcon sx={{ flexShrink: 0, fontSize: "16px", ml: "0.5em" }} />
                  </Link>
                ))}
              </Column>
            </EventInfoRow>
          )}
          <EventInfoRow>
            <Tooltip title="Prüfort" arrow placement="top">
              <PlaceOutlinedIcon
                fontSize="small"
                sx={{ alignSelf: "flex-start", color: "text.secondary", mt: "2px" }}
              />
            </Tooltip>
            <Column>
              <Typography variant="body2">{examLocation.name}</Typography>
              <Typography variant="body2">{renderAddress(examLocation.postalAddress, { oneLine: true })}</Typography>
            </Column>
          </EventInfoRow>
        </Column>
        <Column spacing="0.5em" sx={{ minWidth: 0 }}>
          <EventInfoRow>
            <Tooltip title="Terminwünsche" arrow placement="top">
              <CalendarMonthOutlinedIcon
                fontSize="small"
                sx={{ alignSelf: "flex-start", color: "text.secondary", mt: "2px" }}
              />
            </Tooltip>
            <Column>
              <Typography variant="body2" color="text.primary">
                Terminwünsche:
              </Typography>
              {preferredDates
                .map((it) => DateTime.fromFormat(it, "yyyy-MM-dd", { locale: "de", zone: "Europe/Berlin" }))
                .sort()
                .map((date, index) => (
                  <Typography key={index} variant="body2">
                    {date.toLocaleString(DateTime.DATE_MED_WITH_WEEKDAY)}
                  </Typography>
                ))}
            </Column>
          </EventInfoRow>
          <EventInfoRow>
            <Tooltip title="Uhrzeit" arrow placement="top">
              <ScheduleOutlinedIcon
                fontSize="small"
                sx={{ alignSelf: "flex-start", color: "text.secondary", mt: "2px" }}
              />
            </Tooltip>
            <Column>
              <Typography variant="body2" color="text.primary">
                Mögliche Uhrzeiten:
              </Typography>
              <Typography variant="body2">
                {preferredTimeOfDay.includes("morning")
                  ? preferredTimeOfDay.includes("afternoon")
                    ? "vormittags oder nachmittags"
                    : "vormittags"
                  : preferredTimeOfDay.includes("afternoon")
                    ? "nachmittags"
                    : /* Should never happen: */ preferredTimeOfDay.join(", ")}
              </Typography>
            </Column>
          </EventInfoRow>
        </Column>
      </Box>
    </Column>
  );
}

function _PopupMenu({
  workflow,
  closeDialog,
}: {
  workflow: TheoryExamWorkflow | PracticalExamWorkflow;
  closeDialog: () => void;
}) {
  const queryClient = useQueryClient();
  const { anchorEl, openMenu, closeMenu } = useMenu();
  const [deleting, setDeleting] = useState(false);

  const deleteWorkflow = async () => {
    setDeleting(true);
    let success = false;
    try {
      await workflowsProvider.delete("workflows", { id: workflow.id });
      success = true;
    } catch (error) {
      console.error(`Failed to delete workflow ${workflow.id}`, error);
      showErrorToast("Fehler beim Löschen der Anfrage.");
    } finally {
      closeMenu();
      // [UX] the menu item should stay disabled while the close menu animation is running ...
      setTimeout(() => setDeleting(false), 500);
    }
    if (success) {
      closeDialog();
      showSuccessToast("Anfrage erfolgreich gelöscht.");
      void queryClient.invalidateQueries(["workflows"]);
    }
  };

  return (
    <div>
      <IconButton sx={{ position: "absolute", top: "17px", right: "57px" }} onClick={openMenu}>
        <MoreVertIcon sx={{ color: "text.secondary" }} />
      </IconButton>
      <Menu
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={closeMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <MenuItem onClick={deleteWorkflow} disabled={deleting}>
          <ListItemIcon>
            {deleting ? <CircularProgress size={24} color="inherit" /> : <DeleteOutlineIcon />}
          </ListItemIcon>
          <ListItemText>Anfrage löschen</ListItemText>
        </MenuItem>
      </Menu>
    </div>
  );
}
